const SESSION_KEY = 'booga_session_token';
const PLAYER_ID_KEY = 'booga_player_id';
const GAUPA_USER_ID_KEY = 'booga_gaupa_user_id';
const USER_PROFILE_KEY = 'booga_user_profile';
const LEGACY_GAUPA_JWT_KEY = 'gaupa_jwt';

const BACKEND_PROFILE_FIELDS = ['email', 'displayName', 'isNewUser'] as const;

export type BackendLogin = {
  token?: string;
  playerId?: number | string | null;
  userId?: string;
  email?: string | null;
  displayName?: string | null;
  isNewUser?: boolean;
};

let lastBackendLogin: BackendLogin | null = null;

export function saveSession({
  token,
  playerId,
  userId,
  email,
  displayName,
  isNewUser,
}: BackendLogin = {}) {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    if (playerId != null) localStorage.setItem(PLAYER_ID_KEY, String(playerId));
    if (userId) localStorage.setItem(GAUPA_USER_ID_KEY, userId);

    const hasProfileFields = email != null || displayName != null || isNewUser != null;
    if (hasProfileFields) {
      const profile = getUserProfile();
      if (email != null && email !== '') profile.email = email;
      if (displayName != null && displayName !== '') profile.displayName = displayName;
      if (isNewUser != null) profile.isNewUser = Boolean(isNewUser);
      localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile));
    }
  } catch {
    /* ignore storage errors */
  }
}

export function applyBackendLogin(login: BackendLogin | null) {
  if (!login) return null;
  saveSession(login);
  lastBackendLogin = getStoredBackendLogin();
  return lastBackendLogin;
}

export function getLastBackendLogin() {
  return lastBackendLogin;
}

export function getStoredBackendLogin(): BackendLogin | null {
  if (!hasSession()) return null;
  const playerIdRaw = getPlayerId();
  return {
    token: getSessionToken(),
    ...(playerIdRaw !== '' ? { playerId: Number(playerIdRaw) || playerIdRaw } : {}),
    ...(getGaupaUserId() ? { userId: getGaupaUserId() } : {}),
    ...getUserProfile(),
  };
}

export function getSessionToken() {
  try {
    return localStorage.getItem(SESSION_KEY) || '';
  } catch {
    return '';
  }
}

export function getPlayerId() {
  try {
    return localStorage.getItem(PLAYER_ID_KEY) || '';
  } catch {
    return '';
  }
}

export function getGaupaUserId() {
  try {
    return localStorage.getItem(GAUPA_USER_ID_KEY) || '';
  } catch {
    return '';
  }
}

export function getUserProfile(): Partial<Pick<BackendLogin, 'email' | 'displayName' | 'isNewUser'>> {
  try {
    const raw = localStorage.getItem(USER_PROFILE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const profile: Partial<Pick<BackendLogin, 'email' | 'displayName' | 'isNewUser'>> = {};
    for (const key of BACKEND_PROFILE_FIELDS) {
      if (parsed[key] != null) (profile as Record<string, unknown>)[key] = parsed[key];
    }
    return profile;
  } catch {
    return {};
  }
}

export function hasSession() {
  return Boolean(getSessionToken());
}

export function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(PLAYER_ID_KEY);
    localStorage.removeItem(GAUPA_USER_ID_KEY);
    localStorage.removeItem(USER_PROFILE_KEY);
    localStorage.removeItem(LEGACY_GAUPA_JWT_KEY);
  } catch {
    /* ignore storage errors */
  }
  lastBackendLogin = null;
}
